import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { fabric } from 'fabric';

interface ExportOptions {
  canvas: fabric.Canvas;
  containerWidth: number;
  containerHeight: number;
  canvasWidth: number;
  canvasHeight: number;
  canvasLeft: number;
  canvasTop: number;
  objectBackground?: string;
}

export async function exportToPNG(options: ExportOptions): Promise<string> {
  const { 
    canvas, 
    containerWidth,
    containerHeight,
    canvasWidth,
    canvasHeight,
    canvasLeft,
    canvasTop,
    objectBackground 
  } = options;

  return new Promise(async (resolve, reject) => {
    try {
      const container = document.createElement('div');
      container.style.cssText = `
        position: relative;
        width: ${containerWidth}px;
        height: ${containerHeight}px;
        background-color: white;
      `;
      document.body.appendChild(container);

      if (objectBackground) {
        const bgImg = new Image();
        bgImg.crossOrigin = 'anonymous';
        bgImg.src = objectBackground;
        
        bgImg.style.cssText = `
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          object-fit: contain;
          pointer-events: none;
        `;
        container.appendChild(bgImg);

        await new Promise((resolve) => {
          bgImg.onload = resolve;
          bgImg.onerror = () => {
            console.error('Failed to load object background image');
            resolve(null);
          };
        });
      }

      const wrapper = document.createElement('div');
      wrapper.style.cssText = `
        position: absolute;
        transform: translate(${canvasLeft}px, ${canvasTop}px);
        width: ${canvasWidth}px;
        height: ${canvasHeight}px;
      `;
      container.appendChild(wrapper);

      const designData = canvas.toDataURL({
        format: 'png',
        quality: 1,
        multiplier: 4
      });

      const designImg = new Image();
      designImg.src = designData;
      designImg.style.cssText = `
        width: 100%;
        height: 100%;
        display: block;
      `;
      wrapper.appendChild(designImg);

      await new Promise((resolve) => {
        designImg.onload = resolve;
      });

      const result = await html2canvas(container, {
        width: containerWidth,
        height: containerHeight,
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        imageTimeout: 0
      });

      document.body.removeChild(container);
      resolve(result.toDataURL('image/png'));
    } catch (error) {
      console.error('Error in exportToPNG:', error);
      reject(error);
    }
  });
}

export async function exportToPDF({ 
  canvas, 
  canvasWidth, 
  canvasHeight 
}: ExportOptions): Promise<Blob> {
  const mmPerInch = 25.4;
  const dpi = 300;
  const widthMM = (canvasWidth * mmPerInch) / dpi;
  const heightMM = (canvasHeight * mmPerInch) / dpi;

  const pdf = new jsPDF({
    orientation: heightMM > widthMM ? 'portrait' : 'landscape',
    unit: 'mm',
    format: [widthMM, heightMM]
  });

  const canvasData = canvas.toDataURL({
    format: 'png',
    multiplier: dpi / 96
  });

  pdf.addImage(canvasData, 'PNG', 0, 0, widthMM, heightMM);
  return pdf.output('blob');
}

export function downloadFile(data: string | Blob, filename: string) {
  const link = document.createElement('a');
  const url = data instanceof Blob ? URL.createObjectURL(data) : data;
  
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  if (data instanceof Blob) {
    URL.revokeObjectURL(url);
  }
}